import { useState, useEffect, useMemo } from 'react';
import {
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Box,
    Typography
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useSwaggerClient } from '../hooks/useSwaggerClient';

/**
 * Generate display name from lodging and additional fields
 */
function generateDisplayName(lodging, roomNumber, selfLodgingAddress) {
    if (!lodging) {
        return '';
    }
    let name = lodging.name;
    if (lodging.self_lodging && selfLodgingAddress) {
        name += ` - ${selfLodgingAddress}`;
    } else if (!lodging.self_lodging && roomNumber) {
        name += ` - Room ${roomNumber}`;
    }
    return name;
}

/**
 * Lodging field input component
 *
 * When activityId and availabilityPk are provided (FareharborDirect), shows a proper UI
 * with autocomplete for lodging, route/stop dropdowns, etc.
 *
 * Otherwise falls back to JSON textarea input.
 *
 * Value structure:
 * {
 *   name: string,           // Generated display name
 *   lodging: number,        // FH lodging pk
 *   lodging_room_number: string,
 *   self_lodging_address: string,
 *   route: number,          // FH route pk
 *   stop: number            // FH stop pk
 * }
 */
export default function LodgingFieldInput({
    value,
    onChange,
    required,
    label = 'Lodging',
    error,
    helperText,
    disabled,
    // FareharborDirect props
    activityId,
    availabilityPk
}) {
    // Check if we should use the FH UI or fallback to JSON
    // Both activityId and availabilityPk must be provided for FH UI
    const useFHUI = Boolean(activityId && availabilityPk);

    // Fetch lodging info from API (only when using FH UI)
    const [lodgingInfo, loading, fetchError] = useSwaggerClient(
        'activities',
        'getLodgingInfo',
        useFHUI ? { activityId, availabilityPk } : null
    );

    // Local state for FH UI selections
    const [selectedLodging, setSelectedLodging] = useState(null);
    const [roomNumber, setRoomNumber] = useState('');
    const [selfLodgingAddress, setSelfLodgingAddress] = useState('');
    const [selectedRoute, setSelectedRoute] = useState(null);
    const [selectedStop, setSelectedStop] = useState(null);

    // State for JSON fallback
    const [jsonString, setJsonString] = useState('');
    const [parseError, setParseError] = useState('');

    // Initialize FH UI from value prop
    useEffect(() => {
        if (!useFHUI || !lodgingInfo) {
            return;
        }

        if (value) {
            // Find lodging by pk
            if (value.lodging && !selectedLodging) {
                const lodging = lodgingInfo.lodgings?.find(l => l.pk === value.lodging);
                if (lodging) {
                    setSelectedLodging(lodging);
                }
            }
            // Set room number or address
            if (value.lodging_room_number && !roomNumber) {
                setRoomNumber(value.lodging_room_number);
            }
            if (value.self_lodging_address && !selfLodgingAddress) {
                setSelfLodgingAddress(value.self_lodging_address);
            }
            // Find route by pk
            if (value.route && !selectedRoute) {
                const route = lodgingInfo.routes?.find(r => r.pk === value.route);
                if (route) {
                    setSelectedRoute(route);
                }
            }
        }
    }, [value, lodgingInfo, useFHUI]);

    // Initialize stop after route is set
    useEffect(() => {
        if (!useFHUI || !selectedRoute) {
            return;
        }

        if (value?.stop && !selectedStop) {
            const stops = selectedRoute.stops || [];
            const stop = stops.find(s => s.pk === value.stop);
            if (stop) {
                setSelectedStop(stop);
            }
        }
    }, [value, selectedRoute, useFHUI]);

    // Auto-select route from availability's default route
    useEffect(() => {
        if (!useFHUI || !lodgingInfo?.defaultRoute || selectedRoute) {
            return;
        }

        const route = lodgingInfo.routes?.find(r => r.pk === lodgingInfo.defaultRoute.pk);
        if (route) {
            setSelectedRoute(route);
        }
    }, [lodgingInfo, useFHUI]);

    // Get stops for the selected route (stops are nested inside the route object)
    const stopsForRoute = useMemo(() => {
        if (!selectedRoute) {
            return [];
        }
        return selectedRoute.stops || [];
    }, [selectedRoute]);

    // Auto-select stop from lodging's preferred_pickups
    // preferred_pickups have pickupId, stops have pickupId - match on pickupId
    useEffect(() => {
        if (!useFHUI || !selectedLodging || !selectedRoute || selectedStop) {
            return;
        }

        const preferredPickups = selectedLodging.preferred_pickups || [];
        for (const preferred of preferredPickups) {
            // Match preferred pickup's pickupId to stop's pickupId
            const matchingStop = stopsForRoute.find(s => s.pickupId === preferred.pickupId);
            if (matchingStop) {
                setSelectedStop(matchingStop);
                break;
            }
        }
    }, [selectedLodging, selectedRoute, stopsForRoute, useFHUI]);

    // Update parent value when FH UI selections change
    useEffect(() => {
        if (!useFHUI) {
            return;
        }

        const newValue = {
            name: generateDisplayName(selectedLodging, roomNumber, selfLodgingAddress),
            lodging: selectedLodging?.pk || null,
            lodging_room_number: roomNumber || '',
            self_lodging_address: selfLodgingAddress || '',
            route: selectedRoute?.pk || null,
            stop: selectedStop?.pk || null
        };

        // Only update if values actually changed
        if (
            newValue.lodging !== value?.lodging ||
            newValue.lodging_room_number !== value?.lodging_room_number ||
            newValue.self_lodging_address !== value?.self_lodging_address ||
            newValue.route !== value?.route ||
            newValue.stop !== value?.stop
        ) {
            onChange(newValue);
        }
    }, [selectedLodging, roomNumber, selfLodgingAddress, selectedRoute, selectedStop, useFHUI]);

    // Initialize JSON fallback from value prop
    useEffect(() => {
        if (useFHUI) {
            return;
        }

        if (value && typeof value === 'object') {
            setJsonString(JSON.stringify(value, null, 2));
        } else if (value) {
            setJsonString(String(value));
        } else {
            // Default empty lodging structure
            setJsonString(
                JSON.stringify(
                    {
                        name: '',
                        lodging: null,
                        lodging_room_number: '',
                        self_lodging_address: '',
                        route: null,
                        stop: null
                    },
                    null,
                    2
                )
            );
        }
    }, [value, useFHUI]);

    // FH UI Handlers
    const handleLodgingChange = (event, newLodging) => {
        setSelectedLodging(newLodging);
        setRoomNumber('');
        setSelfLodgingAddress('');
        setSelectedStop(null);
    };

    const handleRouteChange = event => {
        const route = lodgingInfo?.routes?.find(r => r.pk === event.target.value);
        setSelectedRoute(route || null);
        setSelectedStop(null);
    };

    const handleStopChange = event => {
        const stop = stopsForRoute.find(s => s.pk === event.target.value);
        setSelectedStop(stop || null);
    };

    // JSON fallback handler
    const handleJsonChange = event => {
        const newValue = event.target.value;
        setJsonString(newValue);
        setParseError('');

        try {
            const parsed = JSON.parse(newValue);
            if (typeof parsed !== 'object' || parsed === null) {
                setParseError('Must be a valid JSON object');
                return;
            }
            onChange(parsed);
        } catch (err) {
            setParseError('Invalid JSON format');
        }
    };

    // Get pickup time info for display
    const getPickupInfo = () => {
        if (!selectedStop || !selectedRoute) {
            return null;
        }
        return {
            time: selectedStop.time,
            location: selectedStop.name,
            route: selectedRoute.name
        };
    };

    // ===== RENDER =====

    // FH UI loading state
    if (useFHUI && loading) {
        return (
            <Box display="flex" alignItems="center" gap={2}>
                <CircularProgress size={20} style={{ marginRight: 8 }} />
                <Typography variant="body2">Loading lodging options...</Typography>
            </Box>
        );
    }

    // FH UI error state
    if (useFHUI && fetchError) {
        return (
            <Alert severity="error">
                Failed to load lodging options: {fetchError.message || 'Unknown error'}
            </Alert>
        );
    }

    // FH UI
    if (useFHUI && lodgingInfo) {
        const pickupInfo = getPickupInfo();

        return (
            <Box>
                {/* Lodging Autocomplete */}
                <Autocomplete
                    value={selectedLodging}
                    onChange={handleLodgingChange}
                    options={lodgingInfo.lodgings || []}
                    getOptionLabel={option => option?.name || ''}
                    getOptionSelected={(option, val) => option?.pk === val?.pk}
                    disabled={disabled}
                    renderInput={params => (
                        <TextField
                            {...params}
                            label="Hotel / Lodging"
                            required={required}
                            size="small"
                            margin="dense"
                            error={error}
                            helperText={helperText}
                        />
                    )}
                />

                {/* Room Number (if not self_lodging) */}
                {selectedLodging && !selectedLodging.self_lodging && (
                    <TextField
                        label="Room Number"
                        value={roomNumber}
                        onChange={e => setRoomNumber(e.target.value)}
                        disabled={disabled}
                        size="small"
                        margin="dense"
                        fullWidth
                        style={{ marginTop: 8 }}
                    />
                )}

                {/* Contact Address (if self_lodging) */}
                {selectedLodging && selectedLodging.self_lodging && (
                    <TextField
                        label="Contact Address"
                        value={selfLodgingAddress}
                        onChange={e => setSelfLodgingAddress(e.target.value)}
                        disabled={disabled}
                        size="small"
                        margin="dense"
                        fullWidth
                        style={{ marginTop: 8 }}
                        helperText="Enter your pickup/contact address"
                    />
                )}

                {/* Route Selection */}
                {lodgingInfo.routes?.length > 0 && (
                    <FormControl
                        variant="outlined"
                        size="small"
                        margin="dense"
                        fullWidth
                        style={{ marginTop: 8 }}
                        disabled={disabled}
                    >
                        <InputLabel>Pickup Route</InputLabel>
                        <Select
                            value={selectedRoute?.pk || ''}
                            onChange={handleRouteChange}
                            label="Pickup Route"
                        >
                            {lodgingInfo.routes.map(route => (
                                <MenuItem key={route.pk} value={route.pk}>
                                    {route.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                {/* Stop Selection */}
                {selectedRoute && stopsForRoute.length > 0 && (
                    <FormControl
                        variant="outlined"
                        size="small"
                        margin="dense"
                        fullWidth
                        style={{ marginTop: 8 }}
                        disabled={disabled}
                    >
                        <InputLabel>Pickup Location</InputLabel>
                        <Select
                            value={selectedStop?.pk || ''}
                            onChange={handleStopChange}
                            label="Pickup Location"
                        >
                            {stopsForRoute.map(stop => (
                                <MenuItem key={stop.pk} value={stop.pk}>
                                    {stop.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                {/* Pickup Info Display */}
                {pickupInfo && (
                    <Alert severity="info" style={{ marginTop: 12 }}>
                        <Typography variant="body2">
                            <strong>Pickup:</strong> {pickupInfo.time} at {pickupInfo.location}
                            <br />
                            <strong>Route:</strong> {pickupInfo.route}
                        </Typography>
                    </Alert>
                )}
            </Box>
        );
    }

    // JSON Fallback UI
    return (
        <TextField
            label={label}
            value={jsonString}
            onChange={handleJsonChange}
            multiline
            rows={6}
            fullWidth
            required={required}
            disabled={disabled}
            error={error || !!parseError}
            helperText={parseError || helperText || 'Enter lodging info as JSON'}
            InputProps={{
                style: { fontFamily: 'monospace', fontSize: 12 }
            }}
        />
    );
}
